// @ts-nocheck -- FullStory initialization code
/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { match } from "ts-pattern";
import { AnalyticsEvent } from "constants/Analytics";
import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { FloodgateApi } from "api/FloodgateApi";
import gatewayFetch from "utilities/gatewayFetch";
import { AdobeId } from "reducers/CurrentUserReducer";
import { FullStory, init as initFullStory } from "@fullstory/browser";

const isTest = JSON.parse(process.env.IS_TEST ?? "false");
const isSnapshot = navigator.userAgent === "ReactSnap";
const CUSTOMER_IO_WRITE_KEY = process.env.CUSTOMER_IO_WRITE_KEY;
const CUSTOMER_IO_SITE_ID = process.env.CUSTOMER_IO_SITE_ID;
const FULLSTORY_ORG_ID = process.env.FULLSTORY_ORG_ID;

let _cioanalytics: any = {
  page: () => {},
  track: (
    event: PersonalizationEvent,
    properties: Record<string, any> = {}
  ) => {
    console.debug(
      `CustomerIO Tracking is off: Event: ${event} Properties:`,
      properties
    );
  },
  identify: () => {},
};

let _ajoanalytics: any = {
  track: (event: PersonalizationEvent) => {
    console.debug(`AJO Tracking is off: Event: ${event}`);
  },
};

let _fullstoryanalytics: any = {
  identify: (email: string, adobeId: AdobeId) => {
    console.debug(`FullStory Tracking is off: Identifying user: ${adobeId}`);
  },
};

// Only used for tracking events that differ from the name in AnalyticsEvent
export enum PersonalizationEvent {
  UPGRADE_BUTTON_CLICKED = "upgrade_button_clicked",

  ENHANCE_SPEECH_LEGACY_FREE_VIEW_LOADED = "enhance_speech_legacy_free_view_loaded",
  ENHANCE_SPEECH_UNIFIED_VIEW_LOADED = "enhance_speech_unified_view_loaded",

  // C.IO only for now, will drop "express_" when we drop AJO
  PODCAST_PREMIUM_CHECKOUT_COMPLETED = "podcast_premium_checkout_completed",
  PODCAST_PREMIUM_CHECKOUT_CANCELLED = "podcast_premium_checkout_cancelled",
  PODCAST_PREMIUM_CHECKOUT_STARTED = "podcast_premium_checkout_started",
}

// https://wiki.corp.adobe.com/display/marketingtech/Adobe+Experience+Platform+Integration+Guide
// See app/frontend/index.html that adds `window._satellite`
declare global {
  interface Window {
    _satellite: {
      track: (command: string, config: any) => void;
    };
  }
}

const loadAJOSdk = (): Promise<void> => {
  const isTest = JSON.parse(process.env.IS_TEST ?? "false");
  const isSnapshot = navigator.userAgent === "ReactSnap";
  if (!isTest && process.env.AJO_LAUNCH_URL && !isSnapshot) {
    const script = document.createElement("script");
    script.src = "https://www.adobe.com/marketingtech/main.standard.min.js";
    document.body.appendChild(script);
    return new Promise((resolve) => {
      script.onload = (): void => {
        window._satellite.track("pageload", {
          xdm: {},
          data: {},
        });

        _ajoanalytics.track = (event: PersonalizationEvent): void => {
          try {
            window._satellite.track("event", {
              xdm: {},
              data: {
                web: {
                  webInteraction: {
                    name: `podcast/${event}`,
                    URL: window.location.href,
                  },
                },
                _adobe_corpnew: {
                  podcastMetadata: {},
                },
              },
            });
          } catch (error) {
            console.error("Something went wrong with an AJO request", error);
          }
        };
      };

      resolve();
    });
  } else {
    window._satellite = {
      track: (): void => {},
    };
    return Promise.resolve();
  }
};

const loadCustomerIO = (): void => {
  const cioSdk = AnalyticsBrowser.load(
    { writeKey: CUSTOMER_IO_WRITE_KEY },
    {
      integrations: {
        "Customer.io In-App Plugin": {
          siteId: CUSTOMER_IO_SITE_ID,
        },
      },
    }
  );
  _cioanalytics = {
    page: cioSdk.page,
    track: cioSdk.track,
    identify: (email: string, adobeId: AdobeId): void => {
      cioSdk.identify(email, { adobe_id: adobeId });
      gatewayFetch("/api/v1/customer_personalization/enrichment", {
        method: "POST",
      });
    },
  };
  _cioanalytics.page();
};

const loadFullStory = (): void => {
  initFullStory({ orgId: FULLSTORY_ORG_ID }, ({ sessionUrl }) => {
    newrelic.setCustomAttribute("fullstorySessionUrl", sessionUrl);
  });
  _fullstoryanalytics = {
    identify: (email: string, adobeId: AdobeId): void => {
      FullStory.identify(email, { adobe_id: adobeId });
    },
  };
};

export const loadCustomerPersonalization = async (): Promise<void> => {
  // Load Customer.io analytics if enabled
  const isCustomerIoEnabledFF = await FloodgateApi.instance
    .getFeatureEnabled("customer_io_enabled")
    [Symbol.asyncIterator]()
    .next();
  const isCustomerIoEnabled = isCustomerIoEnabledFF.value;

  // Load Customer.IO Tracking
  if (
    !isTest &&
    !isSnapshot &&
    isCustomerIoEnabled &&
    CUSTOMER_IO_WRITE_KEY &&
    CUSTOMER_IO_SITE_ID
  ) {
    loadCustomerIO();
  }

  // Load AJO analytics
  if (!isTest && !isSnapshot) {
    loadAJOSdk();
  }

  const isFullstoryEnabledFF = await FloodgateApi.instance
    .getFeatureEnabled("fullstory_enabled")
    [Symbol.asyncIterator]()
    .next();
  const isFullstoryEnabled = isFullstoryEnabledFF.value;
  if (!isTest && !isSnapshot && isFullstoryEnabled && FULLSTORY_ORG_ID) {
    loadFullStory();
  }
};

// Identify the customer in our personalization services.
export const identifyCustomer = (email: string, adobeId: AdobeId): void => {
  _cioanalytics.identify(email, adobeId);
  _fullstoryanalytics.identify(email, adobeId);
  // NOTE: AJO SDK has integrations with IMS so no need to identify
};

export const derivedPropertiesForCustomerIO = (
  event: AnalyticsEvent
): Record<string, any> =>
  match(event)
    .with(AnalyticsEvent.USER_VISIT_PREMIUM_ENHANCE, () => ({
      hasPremiumAccess: true,
    }))
    .with(AnalyticsEvent.USER_VISIT_FREE_ENHANCE, () => ({
      hasPremiumAccess: false,
    }))
    .otherwise(() => {});

export const mapAnalyticsEventsForAJO = (
  event: AnalyticsEvent
): PersonalizationEvent =>
  match(event)
    .with(
      AnalyticsEvent.USER_CLICKS_UPGRADE_BUTTON,
      () => PersonalizationEvent.UPGRADE_BUTTON_CLICKED
    )
    .with(
      AnalyticsEvent.USER_VISIT_FREE_ENHANCE,
      () => PersonalizationEvent.ENHANCE_SPEECH_LEGACY_FREE_VIEW_LOADED
    )
    .with(
      AnalyticsEvent.USER_VISIT_UNIFIED_FREE_ENHANCE,
      () => PersonalizationEvent.ENHANCE_SPEECH_UNIFIED_VIEW_LOADED
    )
    .otherwise(() => event.toString());

export const mapAnalyticsEventsForCustomerIO = (
  event: AnalyticsEvent
): PersonalizationEvent =>
  match(event)
    .with(
      AnalyticsEvent.USER_CLICKS_UPGRADE_BUTTON,
      () => PersonalizationEvent.UPGRADE_BUTTON_CLICKED
    )
    .with(
      AnalyticsEvent.USER_VISIT_FREE_ENHANCE,
      () => PersonalizationEvent.ENHANCE_SPEECH_LEGACY_FREE_VIEW_LOADED
    )
    .with(
      AnalyticsEvent.USER_VISIT_UNIFIED_FREE_ENHANCE,
      () => PersonalizationEvent.ENHANCE_SPEECH_UNIFIED_VIEW_LOADED
    )
    .with(
      AnalyticsEvent.EXPRESS_PREMIUM_CHECKOUT_STARTED,
      () => PersonalizationEvent.PODCAST_PREMIUM_CHECKOUT_STARTED
    )
    .with(
      AnalyticsEvent.EXPRESS_PREMIUM_CHECKOUT_CANCELLED,
      () => PersonalizationEvent.PODCAST_PREMIUM_CHECKOUT_CANCELLED
    )
    .with(
      AnalyticsEvent.EXPRESS_PREMIUM_CHECKOUT_COMPLETED,
      () => PersonalizationEvent.PODCAST_PREMIUM_CHECKOUT_COMPLETED
    )
    .otherwise(() => event.toString());

export const trackPersonalizationEventFromAdobeAnalyticsEvent = (
  event: AnalyticsEvent,
  properties: Record<string, any> = {}
): void => {
  const extraProperties = derivedPropertiesForCustomerIO(event);
  const trackedProperties = { ...properties, ...extraProperties };

  trackEvent(event, trackedProperties);
};

// Track events in our personalization services.
const trackEvent = (
  event: PersonalizationEvent | string,
  properties: Record<string, any> = {}
): void => {
  _cioanalytics.track(mapAnalyticsEventsForCustomerIO(event), properties);
  _ajoanalytics.track(mapAnalyticsEventsForAJO(event));
};

export const trackPage = (pageUrl?: string): void => {
  _cioanalytics.page(pageUrl);
};
